import { useMemo, useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  Share,
  PlusSquare,
  MoreVertical,
  Download,
  Smartphone,
  CheckCircle2,
  Sparkles,
  Bell,
  Navigation,
} from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * /install — step-by-step "Add to Home Screen" guide.
 *
 * Auto-detects the visitor's platform and shows the matching instructions
 * first, with the other platform collapsed below for sharing. iPhone users
 * cannot trigger native install (Safari has no beforeinstallprompt), but
 * Android Chrome / Edge users can — we hook `beforeinstallprompt` so they
 * can install with one tap.
 */
function detectPlatform() {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return { kind: "unknown" };
  }
  const ua = navigator.userAgent || "";
  const isStandalone =
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true;
  const isIOS = /iPhone|iPad|iPod/.test(ua);
  const isAndroid = /Android/.test(ua);
  // iPadOS 13+ reports as Mac in UA but supports touch
  const isIPadOS = /Macintosh/.test(ua) && navigator.maxTouchPoints > 1;
  return {
    kind: "browser",
    isStandalone,
    isIOS: isIOS || isIPadOS,
    isAndroid,
  };
}

export default function InstallApp() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const nextPath = searchParams.get("next");
  const fromFirstBooking = searchParams.get("first") === "1";
  const env = useMemo(detectPlatform, []);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installResult, setInstallResult] = useState(null); // 'accepted' | 'dismissed'
  const deferredRef = useRef(null);

  useEffect(() => {
    function onBIP(e) {
      e.preventDefault();
      deferredRef.current = e;
      setDeferredPrompt(e);
    }
    window.addEventListener("beforeinstallprompt", onBIP);

    function onInstalled() {
      setInstallResult("accepted");
      deferredRef.current = null;
      setDeferredPrompt(null);
    }
    window.addEventListener("appinstalled", onInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBIP);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const triggerInstall = async () => {
    const promptEvent = deferredRef.current;
    if (!promptEvent) return;
    promptEvent.prompt();
    const choice = await promptEvent.userChoice;
    setInstallResult(choice?.outcome === "accepted" ? "accepted" : "dismissed");
    deferredRef.current = null;
    setDeferredPrompt(null);
  };

  const platformLabel = env.isIOS
    ? "iPhone or iPad"
    : env.isAndroid
    ? "Android"
    : "your phone";

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="install-app-page">
      <header className="sticky top-0 z-10 bg-[#0a0a0a]/90 backdrop-blur border-b border-white/5">
        <div className="max-w-2xl mx-auto px-5 py-4 flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="p-2 -ml-2 rounded-full hover:bg-white/5 transition-colors"
            data-testid="install-back-button"
            aria-label="Go back"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-xl font-bold tracking-tight">Install Link Ride</h1>
            <p className="text-xs text-zinc-500">
              Make Link Ride feel like a real app on {platformLabel}
            </p>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-5 py-8 space-y-8">
        {/* First-booking nudge: customer just booked, route through here to
            push install. Always offer a clear escape hatch to their trip. */}
        {nextPath && (
          <div
            className="rounded-2xl border border-emerald-500/30 bg-gradient-to-br from-emerald-500/10 to-emerald-500/0 p-5 space-y-4"
            data-testid="install-post-booking-banner"
          >
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-6 h-6 text-emerald-400 shrink-0 mt-0.5" />
              <div className="min-w-0">
                <h2 className="text-base font-bold text-emerald-300">
                  {fromFirstBooking ? "Booking confirmed!" : "Almost there"}
                </h2>
                <p className="text-sm text-zinc-300 mt-1 leading-relaxed">
                  Install Link Ride on your home screen so we can ping you the moment a driver heads
                  your way. Takes about 20 seconds — and you can always finish later.
                </p>
              </div>
            </div>
            <Button
              onClick={() => navigate(nextPath)}
              className="w-full bg-[#0047FF] hover:bg-[#003BE0] text-white font-bold rounded-xl"
              data-testid="install-continue-to-trip"
            >
              <Navigation className="w-4 h-4 mr-2" />
              Continue to your trip
            </Button>
          </div>
        )}

        {/* Already installed banner */}
        {env.isStandalone && (
          <div
            className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-5 flex items-start gap-3"
            data-testid="install-already-installed"
          >
            <CheckCircle2 className="w-6 h-6 text-emerald-400 shrink-0 mt-0.5" />
            <div>
              <h2 className="text-base font-bold text-emerald-300">You're all set.</h2>
              <p className="text-sm text-zinc-300 mt-1 leading-relaxed">
                Link Ride is already running as an installed app. Push notifications, faster startup,
                and the full-screen experience are active.
              </p>
            </div>
          </div>
        )}

        {/* Hero */}
        {!env.isStandalone && (
          <section className="space-y-4">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#0047FF] font-bold">
              <Sparkles className="w-3.5 h-3.5" />
              30 seconds · no app store
            </div>
            <h2 className="text-3xl sm:text-4xl font-black tracking-tight leading-[1.1]">
              Turn Link Ride into a real app on your phone.
            </h2>
            <p className="text-base text-zinc-400 leading-relaxed max-w-xl">
              No download. No App Store. Just one button on Android — or a single Safari tap on
              iPhone — and Link Ride lives on your home screen with a real icon, full-screen mode, and
              push notifications when your driver is en route.
            </p>

            <div className="flex flex-wrap gap-2 pt-2">
              <span className="inline-flex items-center gap-1.5 text-xs text-zinc-300 bg-zinc-800/60 border border-white/5 px-3 py-1.5 rounded-full">
                <Bell className="w-3 h-3" /> Push alerts
              </span>
              <span className="inline-flex items-center gap-1.5 text-xs text-zinc-300 bg-zinc-800/60 border border-white/5 px-3 py-1.5 rounded-full">
                <Smartphone className="w-3 h-3" /> Home screen icon
              </span>
              <span className="inline-flex items-center gap-1.5 text-xs text-zinc-300 bg-zinc-800/60 border border-white/5 px-3 py-1.5 rounded-full">
                Full-screen
              </span>
            </div>
          </section>
        )}

        {/* Android Chrome — native install button via beforeinstallprompt */}
        {deferredPrompt && !env.isStandalone && (
          <section
            className="rounded-2xl bg-gradient-to-br from-[#0047FF]/20 to-[#0047FF]/5 border border-[#0047FF]/40 p-5"
            data-testid="install-native-prompt-card"
          >
            <h3 className="text-lg font-bold mb-2 flex items-center gap-2">
              <Download className="w-5 h-5 text-[#0047FF]" />
              One-tap install available
            </h3>
            <p className="text-sm text-zinc-300 mb-4">
              Your browser is ready to install Link Ride directly. Tap below and confirm.
            </p>
            <Button
              onClick={triggerInstall}
              className="bg-[#0047FF] hover:bg-[#0040e6] text-white font-bold px-6 py-6 text-base rounded-xl shadow-lg shadow-[#0047FF]/30"
              data-testid="install-native-prompt-button"
            >
              <Download className="w-5 h-5 mr-2" />
              Install Link Ride
            </Button>
            {installResult === "dismissed" && (
              <p className="text-xs text-amber-400 mt-3" data-testid="install-dismissed-msg">
                No worries — you can always use the manual steps below.
              </p>
            )}
          </section>
        )}

        {installResult === "accepted" && (
          <section
            className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-5 flex items-start gap-3"
            data-testid="install-success-card"
          >
            <CheckCircle2 className="w-6 h-6 text-emerald-400 shrink-0 mt-0.5" />
            <div>
              <h3 className="text-base font-bold text-emerald-300">Link Ride installed.</h3>
              <p className="text-sm text-zinc-300 mt-1 leading-relaxed">
                Look for the Link Ride icon on your home screen — that's your new shortcut. Open it
                from there to get the full-screen experience.
              </p>
            </div>
          </section>
        )}

        {/* iPhone instructions */}
        {!env.isStandalone && (
          <section
            className={env.isIOS ? "" : "opacity-90"}
            data-testid="install-ios-instructions"
          >
            <header className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-zinc-800 border border-white/5 flex items-center justify-center">
                <Smartphone className="w-5 h-5 text-zinc-300" />
              </div>
              <div>
                <h3 className="text-lg font-bold">iPhone &amp; iPad</h3>
                <p className="text-xs text-zinc-500">Must be Safari — not Chrome on iOS.</p>
              </div>
              {env.isIOS && (
                <span className="ml-auto text-[10px] font-bold uppercase tracking-wider bg-[#0047FF] text-white px-2 py-0.5 rounded-full">
                  You're here
                </span>
              )}
            </header>

            <ol className="space-y-3">
              <InstallStep
                num={1}
                title="Tap the Share button"
                body={
                  <>
                    At the bottom of Safari, tap the{" "}
                    <span className="inline-flex items-center gap-1 text-zinc-200 font-semibold">
                      <Share className="w-3.5 h-3.5" /> Share
                    </span>{" "}
                    icon (square with an up arrow).
                  </>
                }
                testId="install-ios-step-1"
              />
              <InstallStep
                num={2}
                title="Scroll and tap “Add to Home Screen”"
                body={
                  <>
                    You may need to scroll down past the share row. Look for the icon that says{" "}
                    <span className="inline-flex items-center gap-1 text-zinc-200 font-semibold">
                      <PlusSquare className="w-3.5 h-3.5" /> Add to Home Screen
                    </span>
                    .
                  </>
                }
                testId="install-ios-step-2"
              />
              <InstallStep
                num={3}
                title="Tap “Add” in the top-right corner"
                body="The name will pre-fill as “Link Ride”. Leave it and tap Add."
                testId="install-ios-step-3"
              />
              <InstallStep
                num={4}
                title="Open Link Ride from your home screen"
                body="It'll launch full-screen, no browser bar — and push notifications will start working once you allow them."
                testId="install-ios-step-4"
              />
            </ol>
          </section>
        )}

        {/* Android instructions */}
        {!env.isStandalone && (
          <section
            className={env.isAndroid ? "" : "opacity-90"}
            data-testid="install-android-instructions"
          >
            <header className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-zinc-800 border border-white/5 flex items-center justify-center">
                <Smartphone className="w-5 h-5 text-zinc-300" />
              </div>
              <div>
                <h3 className="text-lg font-bold">Android</h3>
                <p className="text-xs text-zinc-500">Best on Chrome. Works on Edge too.</p>
              </div>
              {env.isAndroid && (
                <span className="ml-auto text-[10px] font-bold uppercase tracking-wider bg-[#0047FF] text-white px-2 py-0.5 rounded-full">
                  You're here
                </span>
              )}
            </header>

            <ol className="space-y-3">
              <InstallStep
                num={1}
                title="Tap the menu icon"
                body={
                  <>
                    In Chrome, tap the{" "}
                    <span className="inline-flex items-center gap-1 text-zinc-200 font-semibold">
                      <MoreVertical className="w-3.5 h-3.5" /> three-dot
                    </span>{" "}
                    menu in the top-right.
                  </>
                }
                testId="install-android-step-1"
              />
              <InstallStep
                num={2}
                title="Tap “Install app” or “Add to Home screen”"
                body="Newer versions of Chrome show “Install app” directly. Older versions hide it under “Add to Home screen”."
                testId="install-android-step-2"
              />
              <InstallStep
                num={3}
                title="Confirm with “Install”"
                body="Link Ride will appear in your app drawer and on your home screen in a few seconds."
                testId="install-android-step-3"
              />
              <InstallStep
                num={4}
                title="Allow notifications when prompted"
                body="On first launch, tap Allow on the notification permission prompt so you can get ride alerts."
                testId="install-android-step-4"
              />
            </ol>
          </section>
        )}

        {/* Footer / troubleshooting */}
        <section className="pt-4 border-t border-white/5">
          <h3 className="text-sm font-bold text-zinc-300 mb-3">
            Trouble installing?
          </h3>
          <ul className="space-y-2 text-sm text-zinc-400 leading-relaxed">
            <li>
              <span className="text-zinc-300 font-semibold">iPhone:</span> the “Add to Home Screen”
              option ONLY appears in Safari — Chrome on iOS can't install web apps. Open this page
              in Safari and try again.
            </li>
            <li>
              <span className="text-zinc-300 font-semibold">Android:</span> if you don't see
              “Install app”, scroll down and tap “Add to Home screen” instead — same result.
            </li>
            <li>
              <span className="text-zinc-300 font-semibold">Desktop:</span> Chrome and Edge show a
              small{" "}
              <span className="inline-flex items-center gap-1 text-zinc-200 font-semibold">
                <Download className="w-3.5 h-3.5" /> install
              </span>{" "}
              icon at the right end of the URL bar.
            </li>
          </ul>
        </section>
      </main>
    </div>
  );
}

function InstallStep({ num, title, body, testId }) {
  return (
    <li
      className="rounded-xl bg-zinc-900/70 border border-white/5 p-4 flex gap-4"
      data-testid={testId}
    >
      <div className="w-8 h-8 shrink-0 rounded-full bg-[#0047FF]/15 border border-[#0047FF]/40 text-[#0047FF] font-black text-sm flex items-center justify-center">
        {num}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold text-white">{title}</p>
        <p className="text-sm text-zinc-400 mt-1 leading-relaxed">{body}</p>
      </div>
    </li>
  );
}
